/**
 * Video-session allowance — the platform admin's side of the entitlement.
 *
 * The student API decides at session start whether an organisation may run
 * a video session (student/api/_lib/videoEntitlement.js): the add-on must be
 * enabled, and this month's metered minutes must be under `monthlyMinutes`.
 * This module mirrors that arithmetic for the dialog that sets the numbers,
 * so what the admin sees as "remaining" is what the server will allow.
 *
 * Data shape (organizations/{orgId}.videoSessions):
 *   { enabled: boolean, monthlyMinutes: number, maxSessionMinutes?: number }
 * Usage (organizations/{orgId}/usage/{YYYY-MM}.videoMinutes): running total,
 * incremented by the server when a session ends.
 */

export const VIDEO_ALLOWANCE_DEFAULTS = {
  /** Seeded when the add-on is first switched on. */
  monthlyMinutes: 300,
  /** Per-session cap the server applies when none is stored. */
  maxSessionMinutes: 20,
} as const

export interface VideoSessionsSettings {
  enabled: boolean
  monthlyMinutes: number
  maxSessionMinutes: number
}

/** `YYYY-MM` in UTC — the same key the server meters under. */
export function usagePeriodKey(date: Date = new Date()): string {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

const round1 = (n: number) => Math.round(n * 10) / 10

export interface VideoUsageSummary {
  allowance: number
  used: number
  remaining: number
  /** 0–100, capped. */
  percentUsed: number
  /** True when the server would refuse a new session on quota grounds. */
  exhausted: boolean
}

export function summarizeVideoUsage(args: {
  monthlyMinutes?: number | null
  usedMinutes?: number | null
}): VideoUsageSummary {
  const allowance = Number(args.monthlyMinutes) > 0 ? Number(args.monthlyMinutes) : 0
  const usedRaw = Number(args.usedMinutes) > 0 ? Number(args.usedMinutes) : 0
  const remainingRaw = Math.max(0, allowance - usedRaw)
  return {
    allowance,
    used: round1(usedRaw),
    remaining: round1(remainingRaw),
    percentUsed: allowance > 0 ? Math.min(100, Math.round((usedRaw / allowance) * 100)) : 100,
    exhausted: remainingRaw <= 0,
  }
}

export interface AllowanceFormFields {
  enabled: boolean
  monthlyMinutes: string
  maxSessionMinutes: string
}

export type AllowanceParseResult =
  | { ok: true; value: VideoSessionsSettings; warning?: string }
  | { ok: false; field: 'monthlyMinutes' | 'maxSessionMinutes'; message: string }

const wholeNumber = (text: string): number | null => {
  const t = text.trim()
  if (!/^\d+$/.test(t)) return null
  return Number(t)
}

/**
 * Validates the dialog's text fields into the document shape. Switching the
 * add-on off is always allowed regardless of what the number fields hold, so
 * an admin can never be stuck unable to disable.
 */
export function parseAllowanceForm(fields: AllowanceFormFields): AllowanceParseResult {
  const cap = fields.maxSessionMinutes.trim() === ''
    ? VIDEO_ALLOWANCE_DEFAULTS.maxSessionMinutes
    : wholeNumber(fields.maxSessionMinutes)

  if (!fields.enabled) {
    const stored = wholeNumber(fields.monthlyMinutes)
    return {
      ok: true,
      value: {
        enabled: false,
        monthlyMinutes: stored ?? 0,
        maxSessionMinutes: cap && cap >= 1 ? cap : VIDEO_ALLOWANCE_DEFAULTS.maxSessionMinutes,
      },
    }
  }

  const monthly = wholeNumber(fields.monthlyMinutes)
  if (monthly === null) {
    return { ok: false, field: 'monthlyMinutes', message: 'Enter the monthly allowance as a whole number of minutes (0 or more).' }
  }
  if (cap === null || cap < 1) {
    return { ok: false, field: 'maxSessionMinutes', message: 'Per-session cap must be a whole number of at least 1 minute.' }
  }

  return {
    ok: true,
    value: { enabled: true, monthlyMinutes: monthly, maxSessionMinutes: cap },
    warning: monthly === 0
      ? 'Enabled with no minutes: every video session will fall back to voice until an allowance is set.'
      : undefined,
  }
}
